import { ElementType, SpotType } from '@spiaggeit/spit-core'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '@/hooks/store'
import { MapElementWithSectorId } from '@/models/map'
import { QuoteReservation } from '@/models/quote'
import { cartSlice } from '@/store/cartSlice'
import { vehiclePlateErrorSelector } from '@/store/cartSlice/selectors/vehiclePlateErrorSelector'
import { licenseSlice } from '@/store/licenseSlice'
import { ticketSlice } from '@/store/ticketSlice'
import { inputBaseClassName } from '../Input'

interface Props {
  selectedElement: MapElementWithSectorId
  selectedElementReservation: QuoteReservation
}

export function VehiclePlate(props: Props) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const license = useAppSelector(licenseSlice.selectors.license)
  const submitted = useAppSelector(ticketSlice.selectors.submitted)
  const vehiclePlateError = useAppSelector(vehiclePlateErrorSelector)

  const divRef = useRef<HTMLDivElement | null>(null)

  const showError = submitted && vehiclePlateError

  useEffect(() => {
    if (!showError || !divRef.current) {
      return
    }

    divRef.current.scrollIntoView({
      behavior: 'smooth',
    })
  }, [showError])

  if (
    !license ||
    props.selectedElement.type !== ElementType.SPOT ||
    props.selectedElement.subType !== SpotType.PARKING
  ) {
    return null
  }

  const inputProps = {
    'aria-invalid': !!showError,
    'aria-required': license.bookingVehiclePlateRequired,
    id: 'vehicle-plate',
    name: 'vehicle-plate',
  }

  return (
    <div className="border-b border-solid px-3 py-3" ref={divRef}>
      <label className="font-bold leading-5" htmlFor="vehicle-plate">
        {t('map.elementDetail.vehiclePlate.label')}
        {license.bookingVehiclePlateRequired ? ' *' : ''}
      </label>

      <input
        {...inputProps}
        className={inputBaseClassName}
        onChange={(e) => {
          dispatch(cartSlice.actions.setVehiclePlate(e.target.value))
        }}
        value={props.selectedElementReservation.vehiclePlate ?? ''}
      />

      {showError ? (
        <div className="text-red-600">{t(vehiclePlateError)}</div>
      ) : null}
    </div>
  )
}
